import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { GrupPrestasi } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const postData = async (newData: GrupPrestasi) => {
  const response = await api.post("/master/grup-prestasi/store", newData)
  return response.data
}

const patchData = async (newData: GrupPrestasi) => {
  const response = await api.patch("/master/grup-prestasi/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_ref_grup_prestasi: number }) => {
  const response = await api({
    url: "/master/grup-prestasi/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: GrupPrestasi | null
  closeModal: () => void
}) {
  const [form, setForm] = useState({
    id_ref_grup_prestasi: selectedRow?.id_ref_grup_prestasi || 0,
    nama_grup_prestasi: selectedRow?.nama_grup_prestasi || "",
    skor_grup_prestasi: selectedRow?.skor_grup_prestasi || 0,
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listGrupPrestasi"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Grup Prestasi"
          description={`Apakah anda yakin akan menghapus data "${selectedRow?.nama_grup_prestasi}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={
          isPost ? "Insert Data Grup Prestasi" : "Update Data Grup Prestasi"
        }
      />
      <div>
        <Label>Grup Pestasi</Label>
        <Select
          required
          value={form.nama_grup_prestasi}
          onValueChange={(value) => {
            setForm((prev) => ({ ...prev, nama_grup_prestasi: value }))
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={"Pilih Grup Prestasi"}>
              {form.nama_grup_prestasi &&
                ["Individu", "Berkelompok"].find(
                  (value: string) => value === form.nama_grup_prestasi
                )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {["Individu", "Berkelompok"]?.map((value: string) => (
              <SelectItem key={value} value={`${value}`}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Skor</Label>
        <Input
          required
          type="number"
          placeholder="Ketik skor..."
          value={form.skor_grup_prestasi}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              skor_grup_prestasi: Number(event.target.value),
            }))
          }
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
